import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenSource } from '@/api/leadgen.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { usePermissions } from '@/hooks/usePermissions';

export function LeadGenSourcesPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('LEAD_GEN_MANAGE');
  const [sources, setSources] = useState<LeadGenSource[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  const load = useCallback(async () => {
    try {
      setSources(await leadgenApi.listSources());
    } catch (err) {
      showApiError(err, 'Failed to load sources');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!code.trim() || !name.trim()) return;
    try {
      await leadgenApi.createSource({ code, name, active: true });
      notify.success('Source created');
      setCode('');
      setName('');
      load();
    } catch (err) {
      showApiError(err, 'Failed to create source');
    }
  };

  return (
    <div className="page-content">
      <PageHeader
        title="Lead Sources"
        description="Acquisition channel catalog — website, ads, social, partner portal, and API imports."
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Lead Gen', to: '/lead-gen' },
          { label: 'Sources' },
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-5">
          <h3 className="font-bold text-slate-800 mb-4">Configured Sources</h3>
          <ul className="space-y-2">
            {sources.map((s) => (
              <li key={s.id} className="flex justify-between items-center p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div>
                  <p className="font-semibold text-slate-800">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.code}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${s.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600'}`}>
                  {s.active ? 'Active' : 'Inactive'}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {canManage && (
        <div className="card p-5 space-y-4">
          <h3 className="font-bold text-slate-800">Add Custom Source</h3>
          <Input label="Code" placeholder="PARTNER_X" value={code} onChange={(e) => setCode(e.target.value)} />
          <Input label="Name" placeholder="Partner X Referrals" value={name} onChange={(e) => setName(e.target.value)} />
          <Button className="w-full" onClick={create}>Add Source</Button>
        </div>
        )}
      </div>
    </div>
  );
}
